import type { InputHTMLAttributes, ReactNode } from 'react';
import { FormMessage } from './FormMessage';
import { useFieldA11y } from './fieldA11y';

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-invalid'> {
  label: ReactNode;
  helperText?: string;
  error?: string;
  containerClassName?: string;
  inputClassName?: string;
}

export const TextField = ({
  id,
  label,
  helperText,
  error,
  className,
  containerClassName,
  inputClassName,
  required,
  ...inputProps
}: TextFieldProps) => {
  const { fieldId, helperId, errorId, describedByIds } = useFieldA11y({
    id,
    describedBy: inputProps['aria-describedby'],
    helperText,
    error,
  });

  return (
    <label
      className={[
        'form-field',
        error ? 'form-field--invalid' : '',
        containerClassName,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      htmlFor={fieldId}
    >
      <span className="form-field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <input
        id={fieldId}
        className={['form-field__input', inputClassName].filter(Boolean).join(' ')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds}
        {...inputProps}
      />
      <FormMessage id={helperId}>{helperText}</FormMessage>
      <FormMessage id={errorId} tone="error">
        {error}
      </FormMessage>
    </label>
  );
};
